import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tooltip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CountUp from 'react-countup'

const useStyles = makeStyles((theme) => ({
  statCard: {
    borderRadius: 16,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    height: '100%',
    minHeight: 180, // Légèrement augmenté pour mieux contenir
    transition: 'all 0.3s ease',
    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: 'inherit',
    },
  },
  // Styles pour chaque type de carte avec des couleurs différentes
  todayCard: {
    '&::before': {
      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    },
    '& $iconContainer': {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
    },
    '& $amountChip': {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }
  },
  yesterdayCard: {
    '&::before': {
      background: 'linear-gradient(90deg, #f093fb 0%, #f5576c 100%)',
    },
    '& $iconContainer': {
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      boxShadow: '0 4px 15px rgba(240, 147, 251, 0.3)',
    },
    '& $amountChip': {
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    }
  },
  monthCard: {
    '&::before': {
      background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
    },
    '& $iconContainer': {
      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      boxShadow: '0 4px 15px rgba(79, 172, 254, 0.3)',
    },
    '& $amountChip': {
      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    }
  },
  yearCard: {
    '&::before': {
      background: 'linear-gradient(90deg, #43e97b 0%, #38f9d7 100%)',
    },
    '& $iconContainer': {
      background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      boxShadow: '0 4px 15px rgba(67, 233, 123, 0.3)',
    },
    '& $amountChip': {
      background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    }
  },
  cardContent: {
    padding: theme.spacing(2),
    textAlign: 'center',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
    zIndex: 1,
  },
  titleSection: {
    marginBottom: theme.spacing(1),
  },
  title: {
    fontWeight: 700,
    fontSize: '0.9rem',
    color: '#2d3748',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  subtitle: {
    fontWeight: 500,
    fontSize: '0.7rem',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  iconContainer: {
    width: 50,
    height: 50,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 8px auto', // Marge réduite
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  // NOUVELLE SECTION : Statistiques en haut
  topStatsSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: 10,
    border: '1px solid rgba(0, 0, 0, 0.05)',
  },
  topStatItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  topStatLabel: {
    fontSize: '0.6rem',
    color: '#718096',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.2px',
    marginBottom: theme.spacing(0.2),
  },
  topStatValue: {
    fontSize: '0.85rem',
    fontWeight: 700,
  },
  topQuantityValue: {
    color: '#48bb78',
  },
  topSalesValue: {
    color: '#ed8936',
  },
  valueSection: {
    marginBottom: theme.spacing(1),
  },
  value: {
    fontWeight: 800,
    fontSize: '1.5rem',
    background: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1.1,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.3rem',
    },
  },
  valueMillions: {
    fontSize: '1.3rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem',
    },
  },
  valueBillions: {
    fontSize: '1.1rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  amountChip: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '0.7rem',
    padding: '4px 12px',
    borderRadius: 10,
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
    height: 26,
    border: 'none',
    '& .MuiChip-label': {
      padding: '0 4px',
    }
  },
  amountChipMillions: {
    fontSize: '0.65rem',
    padding: '4px 10px',
  },
  amountChipBillions: {
    fontSize: '0.6rem',
    padding: '4px 8px',
  },
  // Ancienne section stats (gardée pour référence mais moins visible)
  statsSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'auto',
    paddingTop: theme.spacing(1),
    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
    opacity: 0.7, // Rendu plus discret
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  statLabel: {
    fontSize: '0.6rem',
    color: '#718096',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.2px',
    marginBottom: theme.spacing(0.2),
  },
  statValue: {
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  quantityValue: {
    color: '#48bb78',
  },
  salesValue: {
    color: '#ed8936',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 100,
    flexDirection: 'column',
  },
  loadingText: {
    marginTop: theme.spacing(1),
    color: '#718096',
    fontSize: '0.75rem',
  },
}));

const RecolteCat = ({ title, data, type, icon, loading, style }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fonction pour formater dynamiquement les montants
  const formatAmount = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    
    if (numAmount >= 1000000000) {
      return {
        value: (numAmount / 1000000000).toFixed(1),
        suffix: 'Md',
        valueClass: `${classes.value} ${classes.valueBillions}`,
        chipClass: `${classes.amountChip} ${classes.amountChipBillions}`
      };
    } else if (numAmount >= 1000000) {
      return {
        value: (numAmount / 1000000).toFixed(1),
        suffix: 'M',
        valueClass: `${classes.value} ${classes.valueMillions}`,
        chipClass: `${classes.amountChip} ${classes.amountChipMillions}`
      };
    } else {
      return {
        value: Math.round(numAmount),
        suffix: '',
        valueClass: classes.value,
        chipClass: classes.amountChip
      };
    }
  };

  // Fonction pour formater les nombres avec séparateurs
  const formatNumber = (number) => {
    return new Intl.NumberFormat('fr-FR').format(number);
  };

  const totalAmount = data ? data.reduce((accumulator, value) => {
    return accumulator + (parseFloat(value.total) || 0);
  }, 0) : 0;

  const totalQuantity = data ? data.reduce((accumulator, value) => {
    return accumulator + (parseInt(value.qtVdu) || 0);
  }, 0) : 0;

  const salesCount = data ? data.length : 0;
  
  // Calcul de la différence si type est fourni
  const displayAmount = type ? totalAmount - type : totalAmount;

  // Formater le montant principal
  const formattedAmount = formatAmount(displayAmount);

  // Déterminer la classe de style basée sur le titre
  const getCardStyle = () => {
    switch (title.toLowerCase()) {
      case "aujourd'hui":
        return classes.todayCard;
      case "hier":
        return classes.yesterdayCard;
      case "mois en cours":
        return classes.monthCard;
      case "année en cours":
        return classes.yearCard;
      default:
        return classes.todayCard;
    }
  };

  if (loading) {
    return (
      <Card className={`${classes.statCard} ${getCardStyle()}`} style={style}>
        <CardContent className={classes.cardContent}>
          <div className={classes.loadingContainer}>
            <CircularProgress size={30} style={{ color: '#667eea' }} />
            <Typography className={classes.loadingText}>
              Chargement...
            </Typography>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${classes.statCard} ${getCardStyle()}`} style={style}>
      <CardContent className={classes.cardContent}>
        {/* En-tête avec titre */}
        <Box className={classes.titleSection}>
          <Typography variant="h6" className={classes.title}>
            {title}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            Performance
          </Typography>
        </Box>

        {/* NOUVELLE SECTION : Statistiques en haut bien visibles */}
        <Box className={classes.topStatsSection}>
          {/* Quantité vendue */}
          <Tooltip title={`Quantité totale vendue: ${totalQuantity}`} arrow>
            <Box className={classes.topStatItem}>
              <Typography className={classes.topStatLabel}>
                QUANTITÉ
              </Typography>
              <Typography className={`${classes.topStatValue} ${classes.topQuantityValue}`}>
                {totalQuantity > 0 ? (
                  <CountUp 
                    start={0} 
                    end={totalQuantity} 
                    duration={1.5} 
                    separator=" "
                  />
                ) : (
                  '0'
                )}
              </Typography>
            </Box>
          </Tooltip>

          {/* Séparateur vertical */}
          <Box sx={{ 
            width: 1, 
            height: 20, 
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            mx: 0.5 
          }} />

          {/* Nombre de ventes */}
          <Tooltip title={`Nombre total de transactions: ${salesCount}`} arrow>
            <Box className={classes.topStatItem}>
              <Typography className={classes.topStatLabel}>
                VENTES
              </Typography>
              <Typography className={`${classes.topStatValue} ${classes.topSalesValue}`}>
                {salesCount > 0 ? (
                  <CountUp 
                    start={0} 
                    end={salesCount} 
                    duration={1.5} 
                    separator=" "
                  />
                ) : (
                  '0'
                )}
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        {/* Icône centrale */}
        {icon && (
          <Box className={classes.iconContainer}>
            {React.cloneElement(icon, { 
              style: { 
                fontSize: 22,
                color: 'white' 
              } 
            })}
          </Box>
        )}

        {/* Section valeur principale */}
        <Box className={classes.valueSection}>
          {/* Montant total avec animation */}
          <Tooltip title={`${formatNumber(displayAmount)} FCFA`} arrow>
            <Typography variant="h2" className={formattedAmount.valueClass}>
              <CountUp 
                start={0} 
                end={parseFloat(formattedAmount.value)} 
                duration={2} 
                separator=" "
                decimals={formattedAmount.suffix ? 1 : 0}
                suffix={formattedAmount.suffix ? ` ${formattedAmount.suffix}` : ""}
              />
            </Typography>
          </Tooltip>

          {/* Chip avec devise */}
          <Chip
            label={`${formattedAmount.suffix ? `${formattedAmount.value} ${formattedAmount.suffix}` : formatNumber(parseFloat(formattedAmount.value))} FCFA`}
            className={formattedAmount.chipClass}
            variant="default"
            size="small"
          />
        </Box>

        {/* Ancienne section stats (gardée mais discrète) */}
        <Box className={classes.statsSection}>
          <Box className={classes.statItem}>
            <Typography className={classes.statLabel}>
              QTÉ
            </Typography>
            <Typography className={`${classes.statValue} ${classes.quantityValue}`}>
              {formatNumber(totalQuantity)}
            </Typography>
          </Box>
          
          <Box sx={{ 
            width: 1, 
            height: 20, 
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            mx: 0.5 
          }} />

          <Box className={classes.statItem}>
            <Typography className={classes.statLabel}>
              VTS
            </Typography>
            <Typography className={`${classes.statValue} ${classes.salesValue}`}>
              {formatNumber(salesCount)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default RecolteCat;